import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ArrowPathIcon,
  TrashIcon,
  PlayIcon,
  DocumentTextIcon,
  CalendarIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import { webhookService, WebhookEvent, WebhookEventFilters, WebhookStats } from '../../services/webhookService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Table, Pagination, Column } from '../../components/UI/Table';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import toast from 'react-hot-toast';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const WebhookDashboardPage: React.FC = () => {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [stats, setStats] = useState<WebhookStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<WebhookEvent | null>(null);
  const [retryingEvents, setRetryingEvents] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<WebhookEventFilters>({
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, [filters]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await webhookService.getWebhookEvents(filters);
      setEvents(response.events);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch webhook events:', error);
      toast.error('Failed to load webhook events');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await webhookService.getWebhookStats();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch webhook stats:', error);
    }
  };

  const handleRetryEvent = async (eventId: string) => {
    try {
      setRetryingEvents(prev => new Set(prev).add(eventId));
      await webhookService.retryWebhookEvent(eventId);
      toast.success('Webhook event retry initiated');
      fetchEvents(); // Refresh the list
    } catch (error) {
      console.error('Failed to retry webhook event:', error);
      toast.error('Failed to retry webhook event');
    } finally {
      setRetryingEvents(prev => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
    }
  };

  const handleSendTestWebhook = async () => {
    try {
      await webhookService.sendTestWebhook({
        eventType: 'end-of-call-report',
        callId: `test_${Date.now()}`,
        leadPhone: '+15551234567',
      });
      toast.success('Test webhook sent successfully');
      fetchEvents(); // Refresh the list
    } catch (error) {
      console.error('Failed to send test webhook:', error);
      toast.error('Failed to send test webhook');
    }
  };

  const handleCleanupOldEvents = async () => {
    try {
      const result = await webhookService.cleanupOldEvents(30);
      toast.success(`Cleaned up ${result.deletedCount} old webhook events`);
      fetchEvents(); // Refresh the list
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Failed to cleanup old events:', error);
      toast.error('Failed to cleanup old events');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL webhook events? This action cannot be undone.')) {
      return;
    }
    
    try {
      const result = await webhookService.deleteAllEvents();
      toast.success(`Deleted ${result.deletedCount} webhook events`);
      fetchEvents(); // Refresh the list
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Failed to delete all events:', error);
      toast.error('Failed to delete all events');
    }
  };

  const handleFilterChange = (key: keyof WebhookEventFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Define table columns
  const columns: Column[] = [
    {
      key: 'event_type',
      header: 'Event Type',
      render: (event) => (
        <span className="font-medium text-foreground">{webhookService.formatEventType(event.event_type)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (event) => (
        <StatusBadge status={event.status} type="webhook" variant="default" />
      ),
    },
    {
      key: 'received_at',
      header: 'Received',
      render: (event) => (
        <span className="text-sm text-foreground">
          {new Date(event.received_at).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'processed_at',
      header: 'Processed',
      render: (event) => (
        event.processed_at ? (
          <span className="text-sm text-foreground">
            {new Date(event.processed_at).toLocaleString()}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )
      ),
    },
    {
      key: 'retry_count',
      header: 'Retries',
      render: (event) => (
        <span className="text-sm text-foreground">{event.retry_count || 0}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (event) => (
        <div className="flex space-x-2">
          <Button
            onClick={() => {
              setSelectedEvent(event);
              setShowEventModal(true);
            }}
            variant="outlined"
            size="sm"
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          {event.status === 'failed' && (
            <Button
              onClick={() => handleRetryEvent(event.id)}
              variant="outlined"
              size="sm"
              disabled={retryingEvents.has(event.id)}
              loading={retryingEvents.has(event.id)}
            >
              <ArrowPathIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
      align: 'right' as const,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="relative overflow-hidden px-8 py-12">
        <CardContent className="relative">
          <h1 className="text-4xl font-bold text-foreground">
            Webhook Dashboard
          </h1>
          <p className="mt-3 text-lg text-muted-foreground">
            Monitor and manage webhook events from Telnyx and other providers.
          </p>
          <div className="mt-6 flex space-x-3">
            <Button
              onClick={handleSendTestWebhook}
              variant="outlined"
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              Send Test Webhook
            </Button>
            <Button
              onClick={handleCleanupOldEvents}
              variant="outlined"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Cleanup Old Events
            </Button>
            <Button
              onClick={handleDeleteAll}
              disabled={!stats || stats.total === 0}
              variant="destructive"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete All Events
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-card opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="rounded-xl p-3 bg-primary/10 shadow-sm">
                  <DocumentTextIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-foreground">
                      {stats.total.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-medium text-muted-foreground">Total Events</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6">
              <div className="absolute inset-0 bg-gradient-to-br from-success/10 to-card opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="rounded-xl p-3 bg-success/10 shadow-sm">
                  <CheckCircleIcon className="h-6 w-6 text-success" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-foreground">
                      {stats.processed.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-medium text-muted-foreground">Processed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6">
              <div className="absolute inset-0 bg-gradient-to-br from-destructive/10 to-card opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="rounded-xl p-3 bg-destructive/10 shadow-sm">
                  <XCircleIcon className="h-6 w-6 text-destructive" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-foreground">
                      {stats.failed.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-medium text-muted-foreground">Failed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6">
              <div className="absolute inset-0 bg-gradient-to-br from-warning/10 to-card opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="rounded-xl p-3 bg-warning/10 shadow-sm">
                    <ClockIcon className="h-6 w-6 text-warning" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-foreground">
                      {stats.pending.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-medium text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">Filters</h3>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outlined"
              size="sm"
              className={`ml-4 ${showFilters ? 'mb-4' : ''}`}
            >
              <FunnelIcon className="-ml-0.5 mr-2 h-4 w-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </div>

          {showFilters && (
            <div className="border-t border-border pt-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Event Type
                  </label>
                  <select
                    value={filters.eventType || ''}
                    onChange={(e) => handleFilterChange('eventType', e.target.value || undefined)}
                    className="select"
                  >
                    <option value="">All Event Types</option>
                    <option value="end-of-call-report">End of Call Report</option>
                <option value="conversation-update">Conversation Update</option>
                <option value="speech-update">Speech Update</option>
                <option value="tool-calls">Tool Calls</option>
                <option value="status-update">Status Update</option>

                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Status
                  </label>
                  <select
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                    className="select"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="processed">Processed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) => handleFilterChange('startDate', e.target.value || undefined)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) => handleFilterChange('endDate', e.target.value || undefined)}
                    className="input"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <ServerIcon className="h-8 w-8 text-primary" />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-foreground">No webhook events found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {filters.eventType || filters.status || filters.startDate || filters.endDate
                ? 'No events match your current filters.'
                : 'Webhook events will appear here as they are received.'
              }
            </p>
          </div>
        ) : (
          <Table
            data={events}
            columns={columns}
            loading={loading}
            emptyState={
              <div className="text-center py-12">
                <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <ServerIcon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="mt-4 text-sm font-semibold text-foreground">No webhook events found</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {filters.eventType || filters.status || filters.startDate || filters.endDate
                    ? 'No events match your current filters.'
                    : 'Webhook events will appear here as they are received.'
                  }
                </p>
              </div>
            }
          />
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            showInfo
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
          />
        )}
      </Card>

      {/* Event Details Modal */}
      <Modal
        isOpen={showEventModal && !!selectedEvent}
        onClose={() => setShowEventModal(false)}
        title="Webhook Event Details"
        size="xl"
      >
        {selectedEvent && (
          <div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Event Type</label>
                    <p className="mt-1 text-sm text-foreground">
                      {webhookService.formatEventType(selectedEvent.event_type)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Status</label>
                    <div className="mt-1">
                      <StatusBadge status={selectedEvent.status} type="webhook" variant="default" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Event ID</label>
                    <p className="mt-1 text-sm text-foreground font-mono">{selectedEvent.event_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Retry Count</label>
                    <p className="mt-1 text-sm text-foreground">{selectedEvent.retry_count}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Received At</label>
                    <p className="mt-1 text-sm text-foreground">
                      {webhookService.formatDate(selectedEvent.received_at)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Processed At</label>
                    <p className="mt-1 text-sm text-foreground">
                      {selectedEvent.processed_at 
                        ? webhookService.formatDate(selectedEvent.processed_at)
                        : 'Not processed'
                      }
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Payload</label>
                  <div className="bg-muted border border-border rounded-md p-3 max-h-96 overflow-y-auto">
                    <pre className="text-xs text-foreground whitespace-pre-wrap">
                      {JSON.stringify(selectedEvent.payload, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
              
            <div className="mt-6 flex justify-end space-x-3">
              {selectedEvent.status === 'failed' && (
                <Button
                  onClick={() => {
                    handleRetryEvent(selectedEvent.id);
                    setShowEventModal(false);
                  }}
                  disabled={retryingEvents.has(selectedEvent.id)}
                  variant="primary"
                  className="bg-success hover:bg-success/90 focus:ring-success"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Retry Event
                </Button>
              )}
              <Button
                onClick={() => setShowEventModal(false)}
                variant="outlined"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WebhookDashboardPage;